export const APP_NAME = import.meta.env.VITE_APP_NAME || 'ApnaBlock';
export const APP_TAGLINE =
  import.meta.env.VITE_APP_TAGLINE || 'Connected Communities Simplified';

export function getApiBaseUrl(): string {
  const envUrl = import.meta.env.VITE_API_URL;
  if (envUrl && typeof envUrl === 'string' && envUrl.trim() !== '') {
    const trimmed = envUrl.trim();
    // Prevent mixed content when app is served over HTTPS
    if (
      typeof window !== 'undefined' &&
      window.location.protocol === 'https:' &&
      trimmed.startsWith('http://') &&
      !trimmed.includes('localhost') &&
      !trimmed.includes('127.0.0.1')
    ) {
      return trimmed.replace('http://', 'https://');
    }
    return trimmed;
  }

  if (typeof window !== 'undefined') {
    const { protocol, hostname } = window.location;
    if (hostname === 'localhost' || hostname === '127.0.0.1' || /^\d+\.\d+\.\d+\.\d+$/.test(hostname)) {
      return `${protocol}//${hostname}:4000/api/v1`;
    }
  }

  return 'http://localhost:4000/api/v1';
}

export const API_BASE_URL = getApiBaseUrl();

export const ROLES = {
  SUPER_ADMIN: 'SUPER_ADMIN',
  PRESIDENT: 'PRESIDENT',
  VICE_PRESIDENT: 'VICE_PRESIDENT',
  GENERAL_SECRETARY: 'GENERAL_SECRETARY',
  JOINT_SECRETARY: 'JOINT_SECRETARY',
  TREASURER: 'TREASURER',
  EXECUTIVE_MEMBER: 'EXECUTIVE_MEMBER',
  AUDITOR: 'AUDITOR',
  COMMITTEE_MEMBER: 'COMMITTEE_MEMBER',
  RESIDENT: 'RESIDENT',
  OWNER: 'OWNER',
  TENANT: 'TENANT',
  GUEST: 'GUEST',
  WATCHMAN: 'WATCHMAN',
} as const;

export type AppRole = (typeof ROLES)[keyof typeof ROLES];

export const QUERY_KEYS = {
  authMe: ['auth', 'me'] as const,
  societies: ['societies'] as const,
  society: (id: string) => ['societies', id] as const,
  complaints: ['complaints'] as const,
  complaint: (id: string) => ['complaints', id] as const,
  complaintDashboard: ['complaints', 'dashboard'] as const,
  serviceCategories: ['services', 'categories'] as const,
  serviceProviders: ['services', 'providers'] as const,
  serviceProvider: (id: string) => ['services', 'providers', id] as const,
  serviceRequests: ['services', 'requests'] as const,
  serviceRequest: (id: string) => ['services', 'requests', id] as const,
  serviceBilling: (id: string) => ['services', 'requests', id, 'billing'] as const,
  societyStaff: ['staff'] as const,
  societyStaffMember: (id: string) => ['staff', id] as const,
  visitorDesk: ['visitors', 'desk'] as const,
  visitors: ['visitors'] as const,
  visitor: (id: string) => ['visitors', id] as const,
  gatePasses: ['visitors', 'gate-passes'] as const,
  events: ['events'] as const,
  event: (id: string) => ['events', id] as const,
  eventDashboard: ['events', 'dashboard'] as const,
  eventCalendar: (from: string, to: string) => ['events', 'calendar', from, to] as const,
  eventRegistrations: (id: string) => ['events', id, 'registrations'] as const,
  notices: ['notices'] as const,
  notice: (id: string) => ['notices', id] as const,
  noticeDashboard: ['notices', 'dashboard'] as const,
  whatsappMessages: ['whatsapp', 'messages'] as const,
  notificationPreferences: ['mobile', 'notification-preferences'] as const,
  elections: ['elections'] as const,
  election: (id: string) => ['elections', id] as const,
  electionDashboard: ['elections', 'dashboard'] as const,
  electionCandidates: (id: string, params?: unknown) =>
    ['elections', id, 'candidates', params] as const,
  electionTurnout: (id: string) => ['elections', id, 'turnout'] as const,
  electionResults: (id: string) => ['elections', id, 'results'] as const,
  houses: ['houses'] as const,
  house: (id: string) => ['houses', id] as const,
  houseDashboard: ['houses', 'dashboard'] as const,
  housePresidentOwner: ['houses', 'president-owner'] as const,
  residents: ['residents'] as const,
  resident: (id: string) => ['residents', id] as const,
  residentDashboard: ['residents', 'dashboard'] as const,
  meetings: ['meetings'] as const,
  meeting: (id: string) => ['meetings', id] as const,
  meetingDashboard: ['meetings', 'dashboard'] as const,
  financeAccounts: ['finance', 'accounts'] as const,
  financeAccount: (id: string) => ['finance', 'accounts', id] as const,
  financeTransactions: ['finance', 'transactions'] as const,
  financeDashboard: ['finance', 'dashboard'] as const,
  collections: ['collections'] as const,
  collection: (id: string) => ['collections', id] as const,
  collectionDashboard: ['collections', 'dashboard'] as const,
  expenses: ['expenses'] as const,
  expense: (id: string) => ['expenses', id] as const,
  expenseDashboard: ['expenses', 'dashboard'] as const,
  expenseCategories: ['expenses', 'categories'] as const,
  expenseVendors: ['expenses', 'vendors'] as const,
  societyFundCampaigns: ['collections', 'society-fund'] as const,
  societyFundCampaign: (id: string) => ['collections', 'society-fund', id] as const,
  societyFundDues: (id: string) => ['collections', 'society-fund', id, 'dues'] as const,
  notifications: ['notifications'] as const,
  notificationUnread: ['notifications', 'unread'] as const,
  roles: ['roles'] as const,
  role: (id: string) => ['roles', id] as const,
  permissions: ['permissions'] as const,
  rbacAuditLogs: ['rbac', 'audit-logs'] as const,
  googleCalendarStatus: ['integrations', 'google-calendar'] as const,
  licenseStatus: ['license', 'status'] as const,
  licenseInvoices: ['license', 'invoices'] as const,
  governanceSummary: ['governance', 'summary'] as const,
  governanceOfficers: ['governance', 'officers'] as const,
  governanceResignations: ['governance', 'resignations'] as const,
  governancePetitions: ['governance', 'petitions'] as const,
} as const;

export const STORAGE_KEYS = {
  auth: 'society-auth',
  ui: 'society-ui',
  branding: 'society-branding',
  language: 'society-language',
  biometricEnabled: 'society-biometric-enabled',
  biometricCredentials: 'society-biometric-credentials',
} as const;
